import axios from "axios";
import { notification } from "antd";

// Crear una instancia de axios con optimizaciones de rendimiento
const api = axios.create({
  //baseURL: "http://localhost:8000/api", // URL directa del backend Laravel
  baseURL: "https://mogotaxsas.com/api", // URL directa del backend Laravel

  timeout: 120000, // 2 minutos para consultas pesadas como filtros de rango de fechas
  // Optimizaciones de rendimiento para XHR
  maxRedirects: 5,
  maxContentLength: 50 * 1024 * 1024, // 50MB max
  maxBodyLength: 50 * 1024 * 1024, // 50MB max
  // Configuración de adaptador para optimizar rendimiento
  adapter: "xhr",
  // Configuración de transformación optimizada
  transformResponse: [
    function (data) {
      // Parsear JSON de forma sincrónica para evitar problemas con Promises
      if (typeof data === "string") {
        try {
          return JSON.parse(data);
        } catch (error) {
          console.warn("Error parsing JSON response:", error);
          return data;
        }
      }
      return data;
    },
  ],
});

// Función para cerrar sesión y redirigir al login (optimizada)
const handleLogout = () => {
  // Limpiar todos los datos de sesión
  localStorage.clear();

  // Usar requestIdleCallback para mostrar notificación sin bloquear
  const scheduleLogoutNotification = (callback) => {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(callback, { timeout: 2000 });
    } else {
      setTimeout(callback, 50);
    }
  };

  scheduleLogoutNotification(() => {
    // Mostrar notificación
    notification.error({
      message: "Sesión expirada",
      description:
        "Tu sesión ha expirado o no tienes autorización. Por favor inicia sesión nuevamente.",
      placement: "topRight",
      duration: 3,
    });

    // Esperar un momento para que se muestre la notificación antes de redirigir
    setTimeout(() => {
      window.location.href = "/login";
    }, 1000);
  });
};

// Interceptor de solicitud para agregar el accessToken
api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem("accessToken"); // Recuperar el accessToken del localStorage
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`; // Agregar el accessToken en el header Authorization
    }
    return config;
  },
  (error) => {
    return Promise.reject(new Error(error.message || "Error en la solicitud"));
  }
);

// Una sola renovación a la vez: si varias peticiones reciben 401 al mismo tiempo, todas esperan
// el mismo token nuevo. Antes cada una renovaba por su cuenta con el token viejo; la primera lo
// invalidaba y las demás recibían 401 y cerraban la sesión.
let renovacionEnCurso = null;

const renovarToken = (refreshToken) => {
  if (!renovacionEnCurso) {
    renovacionEnCurso = axios
      .post(`${process.env.REACT_APP_API_URL}/auth/refresh-token`, { refreshToken })
      .then((response) => {
        const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
          response.data;

        // Actualizar los tokens en localStorage
        localStorage.setItem("accessToken", newAccessToken);
        localStorage.setItem("refreshToken", newRefreshToken);

        // Despachar un evento personalizado para notificar a los componentes
        window.dispatchEvent(new Event("tokens_updated"));

        return newAccessToken;
      })
      .finally(() => {
        renovacionEnCurso = null;
      });
  }
  return renovacionEnCurso;
};

// Interceptor de respuesta para manejar 401 y refrescar tokens
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Al reemplazar una búsqueda, cancelar la anterior es parte del flujo normal.
    if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    // Verificar si el error fue un 401 y que no se haya reintentado ya
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem("refreshToken");

      if (refreshToken) {
        try {
          const newAccessToken = await renovarToken(refreshToken);

          // Actualizar el header Authorization y reintentar la solicitud original
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Si el refreshToken también falla, cerrar sesión
          handleLogout();
          return Promise.reject(refreshError);
        }
      } else {
        // No hay refreshToken, cerrar sesión
        handleLogout();
        return Promise.reject(new Error("No hay refresh token disponible"));
      }
    }

    // Si sigue siendo un 401 después de reintentar, cerrar sesión
    if (error.response?.status === 401) {
      handleLogout();
      return Promise.reject(error);
    }

    // Manejar errores de red (offline) - optimizado
    if (!error.response) {
      // No mostrar notificación si es el endpoint de ping (medición de latencia)
      const isPingRequest = originalRequest?.url?.includes('/ping/super-basic');
      
      if (!isPingRequest && !originalRequest?.suppressNetworkErrorNotification) {
        // Usar requestIdleCallback para mostrar notificación de error de red
        const scheduleNetworkError = (callback) => {
          if (window.requestIdleCallback) {
            window.requestIdleCallback(callback, { timeout: 1500 });
          } else {
            setTimeout(callback, 25);
          }
        };

        scheduleNetworkError(() => {
          notification.error({
            message: "Error de Red",
            description:
              "No se pudo conectar con el servidor. Verifica tu conexión a Internet.",
            placement: "topRight",
          });
        });
      }

      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Exportar la instancia personalizada de axios
export default api;
